/**
 * @file text-field.component.tsx
 * @description Form-control обертка для работы с UI TextInput и Consta TextField в react-hook-form
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-16
 * @dependencies [react, react-hook-form, @components/ui/base/text-input, @consta/uikit/TextField]
 * @related-files [@components/ui/base/text-input/text-input.tsx]
 */

import { TextInput } from '@components/ui/base/text-input';
import { Controller, Control } from 'react-hook-form';
import { TextField as ConstaTextField } from '@consta/uikit/TextField';
import { TValidationRules } from '@src/types';

/**
 * Props для form-control компонента TextField с интеграцией react-hook-form
 */
interface ITextField extends Omit<React.ComponentProps<typeof ConstaTextField>, 'value' | 'onChange' | 'minRows' | 'maxRows' | 'rows'> {
    /** Control объект из react-hook-form для управления формой */
    control: Control<any>;
    /** Имя поля в форме */
    name: string;
    /** Дополнительный обработчик изменений с именем поля и значением */
    onChangeHandler?: (name: string, value: string) => void;
    /** Поле только для чтения */
    readOnly?: boolean;
    /** Удалять ли поле из формы при размонтировании компонента */
    shouldUnregister?: boolean;
    /** Правила валидации react-hook-form */
    rules?: TValidationRules;
    /** Минимальное количество строк для textarea режима */
    minRows?: number;
    /** Максимальное количество строк для textarea режима */
    maxRows?: number;
    /** Фиксированное количество строк для textarea режима */
    rows?: number;
}

export const TextField = ({
    control,
    name,
    onChangeHandler,
    readOnly = false,
    shouldUnregister = false,
    rules = {},
    minRows,
    maxRows,
    rows,
    ...props
}: ITextField) => {
    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            shouldUnregister={shouldUnregister}
            render={({ field }) => (
                <TextInput
                    {...props}
                    {...(minRows !== undefined && { minRows })}
                    {...(maxRows !== undefined && { maxRows })}
                    {...(rows !== undefined && { rows })}
                    onChange={(value) => {
                        if (readOnly) {
                            return;
                        }

                        field.onChange(value);
                        onChangeHandler && onChangeHandler(name, value as string);
                    }}
                    onBlur={field.onBlur}
                    value={field.value}
                    readOnly={readOnly}
                    {...({} as any)} // Type assertion для обхода Consta типизации
                />
            )}
        />
    );
};
